import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  Divider,
  FormControl,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useCheckout } from "./CheckoutContext";
import { v4 as uuidv4 } from "uuid";

const Addresses = () => {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { state: checkoutState, dispatch } = useCheckout();

  const addresses = checkoutState.shippingDetails;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Find default address
  const defaultAddress = addresses.find((a) => a.isDefault);

  // Show non-defaults
  const otherAddresses = addresses.filter((a) => !a.isDefault);

  const [addressForm, setAddressForm] = useState({
    id: "",
    firstName: "",
    lastName: "",
    company: "",
    address1: "",
    address2: "",
    city: "",
    country: "",
    province: "",
    zipCode: "",
    phone: "",
    isDefault: false,
  });

  const handleToggleForm = () => {
    if (!isEditMode) {
      // If adding new address, clear the form
      setAddressForm({
        id: "",
        firstName: "",
        lastName: "",
        company: "",
        address1: "",
        address2: "",
        city: "",
        country: "",
        province: "",
        zipCode: "",
        phone: "",
        isDefault: false,
      });
    }
    setShowForm((prev) => !prev);
    setIsEditMode(false);
  };

  const handleEditClick = (address) => {
    setAddressForm({ ...address });
    setIsEditMode(true);
  };

  const handleEditFormCancel = () => {
    setIsEditMode(false);
    setShowForm(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSaveAddress = () => {
    if (isEditMode) {
      dispatch({
        type: "EDIT_SHIPPING_ADDRESS",
        payload: addressForm,
      });
    } else {
      dispatch({
        type: "ADD_SHIPPING_ADDRESS",
        payload: { ...addressForm, id: uuidv4() },
      });
    }

    setIsEditMode(false);
    setShowForm(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteButton = (addressToDelete) => {
    const confirmed = window.confirm(
      "Are you sure you wish to delete this address?"
    );
    if (confirmed) {
      dispatch({
        type: "DELETE_SHIPPING_ADDRESS",
        payload: addressToDelete,
      });
      setIsEditMode(false);
      setShowForm(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: "auto",
        mt: 20,
        px: 3,
        pb: 10,
        minHeight: "50vh",
      }}
    >
      <Grid container justifyContent="space-between" alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <Typography variant="h5" sx={{ fontWeight: 400 }}>
            ADDRESSES
          </Typography>
          {showForm && (
            <Box sx={{ mt: 6 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                ADD ADDRESS
              </Typography>

              <Grid container spacing={2}>
                {/* First Name */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    FIRST NAME
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    sx={{ borderRadius: 0 }}
                    variant="outlined"
                    name="firstName"
                    value={addressForm.firstName}
                    onChange={(e) =>
                      setAddressForm({
                        ...addressForm,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                </Grid>

                {/* Last Name */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    LAST NAME
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    name="lastName"
                    value={addressForm.lastName}
                    onChange={(e) =>
                      setAddressForm({
                        ...addressForm,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                </Grid>

                {/* Company */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    COMPANY
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    name="company"
                    value={addressForm.company}
                    onChange={(e) =>
                      setAddressForm({
                        ...addressForm,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                </Grid>

                {/* Address 1 */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    ADDRESS 1
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    name="address1"
                    value={addressForm.address1}
                    onChange={(e) =>
                      setAddressForm({
                        ...addressForm,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                </Grid>

                {/* Address 2 */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    ADDRESS 2
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    name="address2"
                    value={addressForm.address2}
                    onChange={(e) =>
                      setAddressForm({
                        ...addressForm,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    CITY
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    name="city"
                    value={addressForm.city}
                    onChange={(e) =>
                      setAddressForm({
                        ...addressForm,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                </Grid>

                {/* Country */}
                <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    COUNTRY
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      name="country"
                      value={addressForm.country}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                      <MenuItem value="Canada">Canada</MenuItem>
                      <MenuItem value="India">India</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Province */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    PROVINCE
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    name="province"
                    value={addressForm.province}
                    onChange={(e) =>
                      setAddressForm({
                        ...addressForm,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                </Grid>

                {/* Zip Code */}
                <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    POSTAL / ZIP CODE
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    name="zipCode"
                    value={addressForm.zipCode}
                    onChange={(e) =>
                      setAddressForm({
                        ...addressForm,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                  >
                    PHONE
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    name="phone"
                    value={addressForm.phone}
                    onChange={(e) =>
                      setAddressForm({
                        ...addressForm,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                </Grid>

                {/* Default checkbox */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="10px"
                        checked={addressForm.isDefault}
                        onChange={(e) =>
                          setAddressForm({
                            ...addressForm,
                            isDefault: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography
                        sx={{ fontSize: "12px", letterSpacing: 1.2, ml: 0.5 }}
                      >
                        SET AS DEFAULT ADDRESS
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>

              {/* Buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column", // Changed from default row to column
                  gap: 2,
                  mt: 4,
                  alignItems: "flex-start", // Align items to the left
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#111",
                    color: "#fff",
                    borderRadius: 0,
                    px: 5,
                    "&:hover": { backgroundColor: "#333" },
                  }}
                  onClick={handleSaveAddress}
                >
                  ADD ADDRESS
                </Button>
                <Typography
                  variant="subtitle2"
                  onClick={handleToggleForm}
                  sx={{
                    textTransform: "none",
                    color: "#000",
                    mt: 1,
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </Typography>
              </Box>
            </Box>
          )}

          {defaultAddress &&
            Object.values(defaultAddress).some(
              (val) => typeof val === "string" && val.trim() !== ""
            ) && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle2">DEFAULT</Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {[
                    // Combine only if either first or last name exists
                    [defaultAddress?.firstName, defaultAddress?.lastName]
                      .filter(Boolean)
                      .join(" "),
                    defaultAddress?.company,
                    defaultAddress?.address1,
                    defaultAddress?.address2,
                    [defaultAddress?.city, defaultAddress?.zipCode]
                      .filter(Boolean)
                      .join(" "),
                    defaultAddress?.country,
                  ]
                    .filter((line) => line && line.trim() !== "")
                    .map((line, idx) => (
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mt: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => handleEditClick(defaultAddress)}
                    sx={{
                      backgroundColor: "#111",
                      color: "#fff",
                      borderRadius: 0,
                      px: 5,
                      width: { xs: "100%", sm: "auto" },
                      "&:hover": { backgroundColor: "#333" },
                    }}
                  >
                    EDIT
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#111",
                      color: "#fff",
                      borderRadius: 0,
                      px: 5,
                      width: { xs: "100%", sm: "auto" },
                      "&:hover": { backgroundColor: "#333" },
                    }}
                    onClick={() => handleDeleteButton(defaultAddress)}
                  >
                    DELETE
                  </Button>
                </Box>
              </Box>
            )}

          {otherAddresses.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                OTHER ADDRESSES
              </Typography>
              {otherAddresses.map((address) => (
                <Box key={address.id} sx={{ mb: 3 }}>
                  <Typography variant="body1">
                    {[
                      `${address.firstName} ${address.lastName}`,
                      address.company,
                      address.address1,
                      address.address2,
                      `${address.city} ${address.zipCode}`,
                      address.country,
                    ]
                      .filter((line) => line && line.trim() !== "")
                      .map((line, idx) => (
                        <React.Fragment key={idx}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mt: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => handleEditClick(address)}
                      sx={{
                        backgroundColor: "#111",
                        color: "#fff",
                        borderRadius: 0,
                        px: 5,
                        width: { xs: "100%", sm: "auto" },
                        "&:hover": { backgroundColor: "#333" },
                      }}
                    >
                      EDIT
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#111",
                        color: "#fff",
                        borderRadius: 0,
                        px: 5,
                        width: { xs: "100%", sm: "auto" },
                        "&:hover": { backgroundColor: "#333" },
                      }}
                      onClick={() => handleDeleteButton(address)}
                    >
                      DELETE
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Grid>

        {/* Add Address Button */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            textAlign: { xs: "left", sm: "right" },
            mt: isMobile ? 6 : 0,
          }}
        >
          {!showForm && (
            <Button
              variant="contained"
              onClick={handleToggleForm}
              sx={{
                backgroundColor: "#111",
                color: "#fff",
                borderRadius: 0,
                px: 5,
                py: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: "120px", // Added to match other buttons
                fontSize: "0.875rem", // Added to match other buttons
                "&:hover": { backgroundColor: "#333" },
              }}
            >
              ADD ADDRESS
            </Button>
          )}
        </Grid>

        <Grid item xs={12} md={8}>
          {isEditMode && (
            <>
              <Divider sx={{ mt: 8 }} />
              <Box sx={{ mt: 7 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {isEditMode ? "EDIT ADDRESS" : "ADD ADDRESS"}
                </Typography>

                <Grid container spacing={2}>
                  {/* First Name */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      FIRST NAME
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="firstName"
                      value={addressForm.firstName}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* Last Name */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      LAST NAME
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="lastName"
                      value={addressForm.lastName}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* Company */}
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      COMPANY
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="company"
                      value={addressForm.company}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* Address 1 */}
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      ADDRESS 1
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="address1"
                      value={addressForm.address1}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* Address 2 */}
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      ADDRESS 2
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="address2"
                      value={addressForm.address2}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* City */}
                  <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      CITY
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="city"
                      value={addressForm.city}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* Country */}
                  <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      COUNTRY
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        name="country"
                        value={addressForm.country}
                        onChange={(e) =>
                          setAddressForm({
                            ...addressForm,
                            [e.target.name]: e.target.value,
                          })
                        }
                      >
                        <MenuItem value="United Kingdom">
                          United Kingdom
                        </MenuItem>
                        <MenuItem value="Canada">Canada</MenuItem>
                        <MenuItem value="United States">United States</MenuItem>
                        <MenuItem value="India">India</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Province */}
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      PROVINCE
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="province"
                      value={addressForm.province}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* Zip Code */}
                  <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      POSTAL / ZIP CODE
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="zipCode"
                      value={addressForm.zipCode}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* Phone */}
                  <Grid item xs={12} md={6} sx={{ mt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, letterSpacing: 1.2, fontSize: "12px" }}
                    >
                      PHONE
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="phone"
                      value={addressForm.phone}
                      onChange={(e) =>
                        setAddressForm({
                          ...addressForm,
                          [e.target.name]: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* Default checkbox */}
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="10px"
                          checked={addressForm.isDefault}
                          onChange={(e) =>
                            setAddressForm({
                              ...addressForm,
                              isDefault: e.target.checked,
                            })
                          }
                        />
                      }
                      label={
                        <Typography
                          sx={{ fontSize: "12px", letterSpacing: 1.2, ml: 0.5 }}
                        >
                          SET AS DEFAULT ADDRESS
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>

                {/* Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column", // Changed from default row to column
                    gap: 2,
                    mt: 4,
                    alignItems: "flex-start", // Align items to the left
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#111",
                      color: "#fff",
                      borderRadius: 0,
                      px: 5,
                      "&:hover": { backgroundColor: "#333" },
                    }}
                    onClick={handleSaveAddress}
                  >
                    {isEditMode ? "UPDATE ADDRESS" : "ADD ADDRESS"}
                  </Button>
                  <Typography
                    variant="subtitle2"
                    onClick={handleEditFormCancel}
                    sx={{
                      textTransform: "none",
                      color: "#000",
                      mt: 1,
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Addresses;
